// @ts-strict-ignore
import { ITableProvider } from '../table-provider';
import { isConnectionSpanData, ISpanData } from '../span-data';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUtils } from '@article/util/article-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionLevelUtils } from '@project/cae/util/connection-level-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { LabelEditorAssistantStepper, LabelEditorPENOptions } from './label-editor-assistant.component';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

export class LabelEditorAssistantUtils {

  public static getSpecialLabelValue(
    tableProvider: ITableProvider,
    cell: number[],
    selectedPENOptions: LabelEditorPENOptions,
    labelingUtils: LabelingUtils,
    containerService: ContainerService,
  ): string {
    if (!selectedPENOptions.withPE && !selectedPENOptions.withN) {
      return null;
    }
    const spanData: ISpanData = tableProvider.getSpanData(cell[0], cell[1]);
    if (!(spanData && isConnectionSpanData(spanData))) {
      return;
    }
    const connectionCube: Connection = spanData.connection;
    if (!ArticleUtils.isLabelingArticle(spanData.article)) {
      return this.getSpecialLabel(connectionCube, selectedPENOptions);
    } else {
      const backwardConnection: Connection = labelingUtils.getTargetSpacingConnectionByLabelingTexture(connectionCube);
      if (!ArticleUtils.isAdapterArticle(backwardConnection.article)) {
        return this.getSpecialLabel(backwardConnection, selectedPENOptions);
      } else {
        let backwardArticle: ArticleVO;
        const backwardReferenceArticleUsageId: string = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageId(
          connectionCube.articleContainer.articleUsage,
          connectionCube.connectionName,
        );
        if (backwardReferenceArticleUsageId) {
          backwardArticle = containerService.findArticleContainerInGlobal(backwardReferenceArticleUsageId).article;
        } else {
          const backwardArticleUsageId: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(
            backwardConnection.articleContainer.articleUsage,
          );
          backwardArticle = containerService.findArticleContainerInGlobal(backwardArticleUsageId.articleUsageId).article;
        }
        return this.getSpecialLabelDefinedByArticleProperties(backwardArticle, selectedPENOptions, true);
      }
    }
  }

  private static getSpecialLabel(connection: Connection, selectedPENOptions: LabelEditorPENOptions): string {
    const labelDefinedByConnectionProperties: string = this.getSpecialLabelDefinedByConnectionProperties(
      connection,
      selectedPENOptions
    );
    if (labelDefinedByConnectionProperties) {
      return labelDefinedByConnectionProperties;
    }
    const labelDefinedByArticleConnectionsLevel: string = this.getSpecialLabelDefinedByArticleConnectionsLevels(
      connection,
      selectedPENOptions
    );
    if (labelDefinedByArticleConnectionsLevel) {
      return labelDefinedByArticleConnectionsLevel;
    }
    const labelDefinedByArticleProperties: string = this.getSpecialLabelDefinedByArticleProperties(
      connection.article,
      selectedPENOptions,
      false
    );
    if (labelDefinedByArticleProperties) {
      return labelDefinedByArticleProperties;
    }
    return null;
  }

  private static getSpecialLabelDefinedByConnectionProperties(
    connection: Connection,
    selectedPENOptions: LabelEditorPENOptions
  ): string {
    if (!connection) {
      return null;
    }
    if (selectedPENOptions.withPE && ConnectionInfoUtils.isPEJumperConnection(connection.metaElementVO)) {
      return this.getLabelBySymbol(selectedPENOptions.selectedPESymbol);
    }
    if (selectedPENOptions.withN && ConnectionInfoUtils.isNPotentialJumperConnection(connection.metaElementVO)) {
      return this.getLabelBySymbol(selectedPENOptions.selectedNSymbol);
    }
    return null;
  }

  private static getSpecialLabelDefinedByArticleConnectionsLevels(
    connection: Connection,
    selectedPENOptions: LabelEditorPENOptions
  ): string {
    if (!connection) {
      return null;
    }
    const connectionLevel: number = ConnectionLevelUtils.getConnectionLevelByProperty(connection.metaElementVO);
    if (NumberUtils.isNumber(connectionLevel)) {
      const articleAcceptingConnections: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(connection.article, ConnectionAreaVO.SIDE_ACCEPTING);
      for (const connection of articleAcceptingConnections) {
        if (ConnectionLevelUtils.getConnectionLevelByProperty(connection) === connectionLevel) {
          if (selectedPENOptions.withPE && ConnectionInfoUtils.isPEJumperConnection(connection)) {
            return this.getLabelBySymbol(selectedPENOptions.selectedPESymbol);
          }
          if (selectedPENOptions.withN && ConnectionInfoUtils.isNPotentialJumperConnection(connection)) {
            return this.getLabelBySymbol(selectedPENOptions.selectedNSymbol);
          }
        }
      }
    }
    return null;
  }

  private static getSpecialLabelDefinedByArticleProperties(
    article: ArticleVO,
    selectedPENOptions: LabelEditorPENOptions,
    isLabelOnAdapter: boolean
  ): string {
    if (!article) {
      return null;
    }
    const articleAcceptingConnections: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(article, ConnectionAreaVO.SIDE_ACCEPTING);
    const isPEConnectionsDefined: boolean = articleAcceptingConnections.some((c) => ConnectionInfoUtils.isPEJumperConnection(c));
    const isNConnectionsDefined: boolean = articleAcceptingConnections.some((c) => ConnectionInfoUtils.isNPotentialJumperConnection(c));
    if (selectedPENOptions.withPE && ArticleUtils.isArticlePEKlemme(article) && (isLabelOnAdapter || !isPEConnectionsDefined)) {
      return this.getLabelBySymbol(selectedPENOptions.selectedPESymbol);
    }
    if (selectedPENOptions.withN && ArticleUtils.isArticleNKlemme(article) && (isLabelOnAdapter || !isNConnectionsDefined)) {
      return this.getLabelBySymbol(selectedPENOptions.selectedNSymbol);
    }
    return null;
  }

  private static getLabelBySymbol(symbol: string): string {
    const symbolCharCode: number = parseInt(symbol, 10);
    return isNaN(symbolCharCode) ? symbol : String.fromCharCode(symbolCharCode);
  }

  public static getCellLabelListForQuickNumericMarking(
    tableProvider: ITableProvider,
    selectedCells: number[][],
    fromInt: number,
    selectedPENOptions: LabelEditorPENOptions,
    labelingUtils: LabelingUtils,
    containerService: ContainerService,
  ) {
    const seqCells: number[][] = [];
    for (let i: number = 0; i < tableProvider.rowCount; i++) {
      for (let j: number = 0; j < tableProvider.colCount; j++) {
        this.addSelectedCell(selectedCells, i, j, seqCells);
      }
    }
    const stepper: LabelEditorAssistantStepper = {
      labelNr: 0,
    };
    const cellLabelList = [];
    cellLoop: for (let i: number = 0; i < seqCells.length; i++) {
      const cell: number[] = seqCells[i];
      const label: string = this.getQuickNumericMarkingLabelValue(
        tableProvider,
        fromInt,
        selectedPENOptions,
        cell,
        stepper,
        labelingUtils,
        containerService,
      );
      stepper.labelNr++;
      if (label === null || label === '') {
        continue cellLoop;
      }
      cellLabelList.push([cell[0], cell[1], label]);
    }
    return cellLabelList;
  }

  public static addSelectedCell(selectedCells: number[][], rowIndex: number, colIndex: number, seqCells: number[][]): void {
    const cell: number[] = this.getSelectedCell(selectedCells, rowIndex, colIndex);
    if (cell !== null) {
      seqCells.push(cell);
    }
  }

  private static getSelectedCell(selectedCells: number[][], rowIndex: number, colIndex: number): number[] {
    if (!selectedCells) {
      return null;
    }
    for (let i: number = 0; i < selectedCells.length; i++) {
      const cell: number[] = selectedCells[i];
      if (cell[0] === rowIndex && cell[1] === colIndex) {
        return cell;
      }
    }
    return null;
  }

  private static getQuickNumericMarkingLabelValue(
    tableProvider: ITableProvider,
    fromInt: number,
    selectedPENOptions: LabelEditorPENOptions,
    cell: number[],
    stepper: LabelEditorAssistantStepper,
    labelingUtils: LabelingUtils,
    containerService: ContainerService,
  ): string {
    const specialLabelValue: string = this.getSpecialLabelValue(
      tableProvider,
      cell,
      selectedPENOptions,
      labelingUtils,
      containerService,
    );
    if (specialLabelValue) {
      stepper.labelNr--;
      return specialLabelValue;
    }
    return fromInt + stepper.labelNr + '';
  }
}
